/**
 * Auth Hook & Context
 * CCH Care - Version 1.4
 *
 * Manages authentication state:
 * - Login flow (credentials → 2FA → session)
 * - Session validation on app load
 * - Token refresh on session.php response
 * - Logout with cleanup
 * - Facility selection
 * - Registers the active facility's display timezone for date/time
 *   rendering (v1.4)
 *
 * v1.1 changes:
 * - checkSession() redirects to /login after clearAuth() so a 401
 *   on app load or session refresh doesn't leave the dashboard blank
 *
 * v1.2 changes (2026-04-19) — Remember Me:
 * - Auth state now lives in EITHER localStorage (Remember Me ON) or
 *   sessionStorage (Remember Me OFF). Keys are the same in either store.
 * - login() and verify2FA() now accept an opts object with { rememberMe }.
 *   LoginPage already passes this through — no changes needed there.
 * - Multi-tab sync: when localStorage.care_token is cleared in another
 *   tab (logout in tab B), this tab reacts by clearing state + redirecting.
 * - Exports getAuthToken() / getAuthMembershipId() / etc. so api/client.js
 *   can read from whichever store currently holds the session without
 *   duplicating the branching logic.
 *
 * v1.3 changes (2026-04-28) — Public route awareness:
 * - The mount effect previously redirected ANY unauthenticated user to
 *   /login, including users on public pages like /reset-password,
 *   /forgot-password, /activate, /register. This broke the password
 *   reset flow because clicking a reset link from email caused an
 *   immediate redirect to /login before ResetPasswordPage could even
 *   validate the token.
 * - Now: PUBLIC_ROUTES list defines paths that DON'T require a session,
 *   and isPublicRoute() is consulted before any auth-driven redirect.
 *   Same logic applied to clearAuth() and the multi-tab storage handler
 *   to be consistent.
 *
 * v1.4 changes (2026-06-25) — Facility display timezone registration:
 * - Registers the ACTIVE facility's IANA timezone (from the session
 *   facilities payload) with formatters.setDisplayTimezone() so every
 *   date/time helper renders in the facility's local frame rather than
 *   the viewer's browser zone. Without this, an off-site reviewer (e.g.
 *   a US-based supervisor looking at a Jamaica facility) saw every ADL
 *   timestamp in their own browser zone — wrong, and silently so.
 * - "Active facility" resolves from user.active_facility_id (set by
 *   switchFacility), then the stored care_facility_id, then the first
 *   facility in the payload. The registration effect re-runs whenever
 *   facilities or the active id changes, so switchFacility re-registers
 *   the new facility's zone automatically.
 * - Cleared (→ browser-zone fallback) whenever the user is logged out,
 *   so a prior facility's zone never lingers into the next session.
 */

import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { login as apiLogin, verify2FA as apiVerify2FA, validateSession, logout as apiLogout } from '../../api/auth';
import { setDisplayTimezone } from '../../utils/formatters';

// ────────────────────────────────────────────────────────────
// Public routes — no session required
// ────────────────────────────────────────────────────────────
// Keep this in sync with the public routes registered in CareRouter.jsx
// (the ones outside the /dashboard ProtectedRoute wrapper).

const PUBLIC_ROUTES = [
  '/',
  '/login',
  '/get-started',
  '/register',
  '/activate',
  '/forgot-password',
  '/reset-password',
];

function isPublicRoute(pathname) {
  // Exact match for the configured paths above. We don't do prefix
  // matching here because the dashboard tree (which uses prefixes like
  // /dashboard/*) is handled separately via ProtectedRoute and shouldn't
  // be considered public no matter what suffix follows.
  return PUBLIC_ROUTES.includes(pathname);
}

// ────────────────────────────────────────────────────────────
// Auth storage: localStorage (Remember Me) vs sessionStorage
// ────────────────────────────────────────────────────────────
// Session vs persistent storage is chosen at login time based on the
// rememberMe flag. On every read, we check localStorage first (a
// remembered session wins if both stores somehow have a token), then
// sessionStorage. On write, we clear both stores first to prevent a
// stale value in one store from masking a fresh session in the other.

const AUTH_KEYS = ['care_token', 'care_membership_id', 'care_tenant_id', 'care_facility_id', 'care_last_activity'];

function safeGet(store, key) {
  try { return store.getItem(key); } catch { return null; }
}
function safeSet(store, key, val) {
  try { store.setItem(key, val); } catch { /* private browsing / quota */ }
}
function safeRemove(store, key) {
  try { store.removeItem(key); } catch { /* no-op */ }
}

function readAuthField(key) {
  return safeGet(localStorage, key) ?? safeGet(sessionStorage, key);
}

/** Returns the store that currently holds the session token, or null. */
function activeAuthStore() {
  if (safeGet(localStorage,   'care_token')) return localStorage;
  if (safeGet(sessionStorage, 'care_token')) return sessionStorage;
  return null;
}

function clearAllAuthStorage() {
  AUTH_KEYS.forEach(k => {
    safeRemove(localStorage,   k);
    safeRemove(sessionStorage, k);
  });
}

function writeAuth(rememberMe, data) {
  // Always clear BOTH stores first — otherwise a previously-remembered
  // localStorage token could shadow a fresh sessionStorage token.
  clearAllAuthStorage();
  const store = rememberMe ? localStorage : sessionStorage;
  if (data.token)         safeSet(store, 'care_token',         data.token);
  if (data.membership_id) safeSet(store, 'care_membership_id', String(data.membership_id));
  if (data.tenant_id)     safeSet(store, 'care_tenant_id',     String(data.tenant_id));
  if (data.facility_id)   safeSet(store, 'care_facility_id',   String(data.facility_id));
}

// ── Exports for api/client.js (and any other request layer) ─
// These replace direct `localStorage.getItem('care_token')` calls so
// the request interceptor picks up whichever store has the session.
export function getAuthToken()        { return readAuthField('care_token'); }
export function getAuthMembershipId() { return readAuthField('care_membership_id'); }
export function getAuthTenantId()     { return readAuthField('care_tenant_id'); }
export function getAuthFacilityId()   { return readAuthField('care_facility_id'); }

// ────────────────────────────────────────────────────────────
// Context
// ────────────────────────────────────────────────────────────

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser]               = useState(null);
  const [memberships, setMemberships] = useState([]);
  const [facilities, setFacilities]   = useState([]);
  const [permissions, setPermissions] = useState([]);
  const [loading, setLoading]         = useState(true);
  const [error, setError]             = useState(null);

  /**
   * Clear all auth state and optionally redirect to login.
   * Pass redirect=false when the caller handles the redirect.
   *
   * v1.3: Don't redirect away from public routes — a user clearing auth
   * while on /reset-password (e.g., session validation failed for a
   * stale token they had) should stay on /reset-password to complete
   * their flow.
   */
  const clearAuth = useCallback((redirect = true) => {
    clearAllAuthStorage();
    setUser(null);
    setMemberships([]);
    setFacilities([]);
    setPermissions([]);
    if (redirect && !isPublicRoute(window.location.pathname)) {
      window.location.href = '/login';
    }
  }, []);

  /**
   * Validate existing session against the API.
   * Called on app mount and by SessionTimeout's "Stay Logged In" flow.
   */
  const checkSession = useCallback(async () => {
    try {
      setLoading(true);
      const data = await validateSession();

      if (data.valid) {
        // session.php returns a refreshed token — update whichever
        // store currently holds the session (respects Remember Me).
        if (data.token) {
          const store = activeAuthStore() || localStorage;
          safeSet(store, 'care_token', data.token);
        }
        setUser(data.user);
        setMemberships(data.memberships || []);
        setFacilities(data.facilities || []);
        setPermissions(data.permissions || []);
      } else {
        clearAuth(true);
      }
    } catch (e) {
      clearAuth(true);
    } finally {
      setLoading(false);
    }
  }, [clearAuth]);

  /**
   * On mount: check for an existing session in either store.
   *
   * v1.3: Public routes (/reset-password, /forgot-password, /activate,
   * /register, /, /login) MUST work for unauthenticated users. Previously
   * this effect redirected any tokenless user to /login regardless of
   * which page they were on, which broke the password reset flow.
   */
  useEffect(() => {
    const token = getAuthToken();
    if (token) {
      checkSession();
    } else {
      setLoading(false);
      if (!isPublicRoute(window.location.pathname)) {
        window.location.href = '/login';
      }
    }
  }, [checkSession]);

  /**
   * Multi-tab sync: if another tab clears localStorage.care_token
   * (explicit logout, or on writeAuth which clears both stores before
   * writing), log this tab out as well.
   *
   * Note: the `storage` event fires only for cross-tab changes and only
   * for localStorage. sessionStorage is per-tab so needs no sync.
   *
   * v1.3: Stay on the current page if it's public — a logout in another
   * tab shouldn't yank a user off /reset-password mid-flow.
   */
  useEffect(() => {
    function onStorage(e) {
      if (e.storageArea !== localStorage) return;
      if (e.key === 'care_token' && e.newValue === null && e.oldValue) {
        setUser(null);
        setMemberships([]);
        setFacilities([]);
        setPermissions([]);
        if (!isPublicRoute(window.location.pathname)) {
          window.location.href = '/login';
        }
      }
    }
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  /**
   * Register the active facility's display timezone (v1.4).
   *
   * Resolves the active facility from user.active_facility_id (set by
   * switchFacility) → stored care_facility_id → first facility in the
   * payload, then registers that facility's IANA `timezone` so every
   * formatters.js date/time helper renders in the facility frame.
   *
   * Re-runs whenever facilities or the active id changes (so a facility
   * switch re-registers automatically). When unauthenticated / no
   * facility resolves, clears the registration so rendering falls back
   * to the viewer's browser zone rather than a stale prior facility.
   */
  useEffect(() => {
    if (!user || facilities.length === 0) {
      setDisplayTimezone(null);
      return;
    }
    const activeId = user.active_facility_id
      ?? Number(getAuthFacilityId())
      ?? facilities[0]?.facility_id;
    const activeFacility =
      facilities.find(f => f.facility_id === activeId) ?? facilities[0];
    setDisplayTimezone(activeFacility?.timezone ?? null);
  }, [user, facilities]);

  /**
   * Login — Step 1 (credentials → temp token for 2FA)
   *
   * @param {string} email
   * @param {string} password
   * @param {object} [opts]
   * @param {boolean} [opts.rememberMe=false]  Pass through to the API
   *   so the temp token (and later the session token) knows whether
   *   to issue a long-lived 30-day JWT or a short-lived 8-hour one.
   */
  const login = useCallback(async (email, password, opts = {}) => {
    setError(null);
    try {
      const data = await apiLogin(email, password, !!opts.rememberMe);
      return data; // Caller handles 2FA flow
    } catch (e) {
      const msg = e.response?.data?.error || e.message || 'Login failed';
      setError(msg);
      throw e;
    }
  }, []);

  /**
   * Verify 2FA — Step 2 (temp token + code → session token)
   *
   * The rememberMe flag here determines:
   *  1. Which storage the session token lands in (localStorage vs sessionStorage)
   *  2. What JWT expiry the backend issues (verify-2fa.php reads this)
   *
   * The flag is belt-and-suspenders: the temp token from login.php also
   * carries it, so the backend can trust the token payload even if the
   * client omits the flag here.
   */
  const verify2FA = useCallback(async (tempToken, code, opts = {}) => {
    setError(null);
    try {
      const rememberMe = !!opts.rememberMe;
      const data = await apiVerify2FA(tempToken, code, rememberMe);

      const defaultMembership = data.memberships[0];
      const firstFacilityId   = data.facilities?.[0]?.facility_id;

      writeAuth(rememberMe, {
        token:          data.token,
        membership_id:  defaultMembership.membership_id,
        tenant_id:      defaultMembership.tenant_id,
        facility_id:    firstFacilityId,
      });

      // HIPAA activity tracking — start clock fresh on successful auth
      try { localStorage.setItem('care_last_activity', String(Date.now())); } catch {}

      setUser(data.user);
      setMemberships(data.memberships);
      setFacilities(data.facilities || []);

      return data;
    } catch (e) {
      const msg = e.response?.data?.error || e.message || '2FA verification failed';
      setError(msg);
      throw e;
    }
  }, []);

  /**
   * Logout — caller navigates to /login after this resolves
   * (suppress the clearAuth redirect so the caller controls timing)
   */
  const logout = useCallback(async () => {
    try { await apiLogout(); } catch { /* best effort — clear local state anyway */ }
    clearAuth(false);
    window.location.href = '/login';
  }, [clearAuth]);

  /**
   * Switch active facility — update whichever store holds the session.
   */
  const switchFacility = useCallback((facilityId) => {
    const store = activeAuthStore();
    if (store) safeSet(store, 'care_facility_id', String(facilityId));
    const facility = facilities.find(f => f.facility_id === facilityId);
    if (facility) {
      setUser(prev => ({ ...prev, active_facility_id: facilityId }));
    }
  }, [facilities]);

  /**
   * Switch active membership (for multi-tenant users)
   */
  const switchMembership = useCallback(async (membershipId) => {
    const membership = memberships.find(m => m.membership_id === membershipId);
    if (!membership) return;

    const store = activeAuthStore();
    if (store) {
      safeSet(store, 'care_membership_id', String(membershipId));
      safeSet(store, 'care_tenant_id',     String(membership.tenant_id));
    }

    await checkSession();
  }, [memberships, checkSession]);

  /**
   * Check if user has a specific permission
   */
  const hasPermission = useCallback((permission) => {
    if (user?.is_tenant_admin) return true;
    return permissions.includes(permission);
  }, [user, permissions]);

  const value = {
    user,
    memberships,
    facilities,
    permissions,
    loading,
    error,
    isAuthenticated: !!user,
    login,
    verify2FA,
    logout,
    switchFacility,
    switchMembership,
    hasPermission,
    checkSession,
    clearError: () => setError(null),
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

export default useAuth;